import React, { useCallback, useContext, useEffect, useState } from "react";
import { Collapse, useMediaQuery, Tooltip } from "@mui/material";
import clsx from "clsx";
import { Context } from "states/Store";
import i18next from "i18next";
import _ from "lodash";

import useStyles from "style/js-style/containers/administration/roles/privileges/ConfigurationLineStyle";
import SwitchButton from "containers/administration/roles/privileges/common/SwitchButton";
import RessourcePagination from "containers/administration/roles/privileges/common/RessourcePagination";
import EAIcon from "components/common/EAIcon";
import { getAccountRequirements } from "services/edge/AccountService";
import { getDoctypeTranslatedName } from '../../../../../services/common/DocumentUtils';
import { AcquisitionChannel, isSealedDocument } from "services/common/Constants";
import { theme } from "style/globalStyle";


/**
 *
 * @param title
 * @param actions
 * @param type subItem | default | dropAction
 * @param variantBackgroundColor
 * @param isTitleLowerCase boolean
 * @returns {JSX.Element}
 * @constructor
 */
function ConfigurationLine({ title, actions, items = [], subActions, type = "default", variantBackgroundColor = undefined, isTitleLowerCase = false,  authorizedDocumentTypes = []}) {
  const { classes } = useStyles();
  const [isOpen, setIsOpen] = useState(true);
  const max1420screen = useMediaQuery("(max-width:1420px)");
  const [state, dispatch] = useContext(Context);
  const [editableDocTypes, setEditableDocTypes] = useState([]);
  const [answerableDocTypes, setAnswerableDocTypes] = useState([]);
  const [docPage, setDocPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (state.account && type === "dropActionPerDoc" && state.doctypeTraductions) {
      (async function fetchData() {
        try {
          const results = await getAccountRequirements(state.account.id, dispatch);

          const viewableDocTypes = results.content.filter(doc => !isSealedDocument(doc.code));

          const authorizedDocTypes = _.isEmpty(authorizedDocumentTypes) ?
            viewableDocTypes
            :
            viewableDocTypes.filter((doc) => authorizedDocumentTypes.includes(doc.documentId));

          setAnswerableDocTypes(authorizedDocTypes
            .filter((doc) => [AcquisitionChannel.DO, AcquisitionChannel.DO_FO].includes(doc.acquisition))
            .map((x) => {
              return {
                value: x.documentId,
                label: getDoctypeTranslatedName(state.doctypeTraductions, x.code, state.lang) || x.descriptionFr,
              }
            })
          );

          // Unautorized document types are hidden
          setEditableDocTypes(
            authorizedDocTypes.map((doc) => {
              return {
                value: doc.documentId,
                label: getDoctypeTranslatedName(state.doctypeTraductions, doc.code, state.lang) || doc.descriptionFr,
              }
            }).sort((a, b) => a.label?.localeCompare(b.label))
          );

        } catch (error) { }
      })();
    }
  }, [state.account, dispatch, authorizedDocumentTypes, type, state.doctypeTraductions, state.lang]);

  const getAvailableDocTypeIdsForAction = useCallback((name) => {
    const isAnswerButton = name === i18next.t("roles.switchButton.answer");
    const availableDocTypes = isAnswerButton ? answerableDocTypes : editableDocTypes;

    return availableDocTypes.map((doc) => doc.value);
  }, [answerableDocTypes, editableDocTypes]);

  const isActionGrantableOnDocType = useCallback((actionName, docTypeId) => {
    const availableDocTypeIds = getAvailableDocTypeIdsForAction(actionName);

    return !_.isEmpty(availableDocTypeIds) && availableDocTypeIds.includes(docTypeId);
  }, [getAvailableDocTypeIdsForAction])


  const ActionsLine = ({actions, icon, isTitleSubItem, getRessourcesForAction = undefined}) => {
    return (
      <>
        <div className={clsx(classes.titleWithIconWrapper, max1420screen && classes.titleFull)}>
          {icon &&
            <EAIcon icon={icon} className={icon === "arrow/arrow-angle_grey" ? classes.arrowIcon : classes.actionsIcon} />
          }
          <div className={isTitleSubItem ? clsx(!max1420screen && classes.titleSubItem, max1420screen && classes.titleSubItemFull): isTitleLowerCase ? classes.titleLower : classes.title}>
            {title}
          </div>
        </div>

        <div className={classes.blocWrapper} >
          <div className={clsx(classes.buttonWrapper, max1420screen && classes.buttonWrapperFull)} >
            {actions?.map((item, index) => (
              <SwitchButton key={index} 
                name={item.name} 
                action={item.action} 
                isActive={!getRessourcesForAction ? item.isActive : item.isActive(getRessourcesForAction(item))} 
                isDisabled={!getRessourcesForAction ? !item.isGrantable : !item.isGrantable || _.isEmpty(getRessourcesForAction(item))}
                resources={!getRessourcesForAction ? undefined : getRessourcesForAction(item)}
              />
            ))}
          </div>
        </div>
      </>

    )
  }

  switch (type) {
    case 'default':
      return (
        <div className={clsx(classes.rootDefault, max1420screen && classes.rootColumn)} >
          <ActionsLine actions={actions} />
        </div>
      )
    case 'subItem':
      return (
        <div className={classes.rootSubItem} style={variantBackgroundColor ?{backgroundColor: variantBackgroundColor} : undefined}>
          <div className={clsx(classes.rootSubItemInner, max1420screen && classes.rootColumn)}>
            <ActionsLine actions={actions} icon="arrow/arrow-angle_grey" isTitleSubItem />
          </div>
      </div>
      )
    case 'dropAction':
      return (
        <div className={clsx(classes.rootDropActions, max1420screen && classes.rootColumn)}>
          <ActionsLine actions={actions} icon="roles/drop-down-actions" isTitleSubItem />
        </div>
      )
    case 'dropActionSubItem':
      return (
        <div className={classes.rootDropActionsWithList} style={variantBackgroundColor ? {backgroundColor: variantBackgroundColor, position: "relative"} : {position: "relative"}}>
          <div className={clsx(classes.headerWrapper)} onClick={() => setIsOpen(!isOpen)}>
               <EAIcon icon="arrow/arrow-to-up_large" className={isOpen ? classes.caretIcon : classes.caretIconReverse} />
              <ActionsLine actions={actions} icon="arrow/arrow-angle_grey" isTitleSubItem />
          </div>

          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <div className={classes.blocListWrapper} >
              {items.length > 0 &&
                  items.map((item, index) => (
                     <div style={{width: "100%"}}>
                      <ConfigurationLine
                        key={item.name + index}
                        type={item.items ? "dropActionSubItem" : "subItem"}
                        title={item.name}
                        actions={item.actions}
                        items={item.items}
                        variantBackgroundColor={item.items ? theme.palette.grey.grey200 : theme.palette.grey.grey100}
                      />
                      </div>
                  ))}
              
            </div>
          </Collapse>


      </div>
      )
    case 'dropActionPerDoc':
      return (
        <div className={clsx(classes.rootDropActionsWithList)}>
          <div className={classes.headerWrapper} onClick={() => setIsOpen(!isOpen)}>
            <EAIcon icon="arrow/arrow-to-up_large_green" className={isOpen ? classes.caretIcon : classes.caretIconReverse} />

            <ActionsLine actions={actions} icon="roles/drop-down-actions" isTitleSubItem 
              getRessourcesForAction={(item => getAvailableDocTypeIdsForAction(item.name))} />

          </div>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <div className={classes.blocListWrapper} >
              {editableDocTypes && editableDocTypes
                .slice((docPage - 1) * itemsPerPage, docPage * itemsPerPage) // Pagination logic
                .map((docEntry, docEntryIndex) => (
                  <div className={clsx(classes.documentRoles)}>
                    <EAIcon icon={"document_grey2"} className={classes.documentIcon} />
                    <Tooltip title={docEntry.label} placement="top">
                      <span className={classes.documentName}> {docEntry.label}</span>
                    </Tooltip>
                    {subActions.map((actionEntry, actionEntryIndex) => (
                      <SwitchButton
                        key={`${docEntryIndex}bt${actionEntryIndex}`}
                        name={actionEntry.name}
                        action={actionEntry.action}
                        isActive={actionEntry.isActive(docEntry.value)}
                        isDisabled={!actionEntry.isGrantable || !isActionGrantableOnDocType(actionEntry.name, docEntry.value)}
                        resourceId={docEntry.value}
                      />
                    ))}
                  </div>
                ))}
              <RessourcePagination
                totalRecords={editableDocTypes?.length}
                pageSize={itemsPerPage}
                onPageChange={(page) => {
                  setDocPage(page);
                }}
              />
            </div>
          </Collapse>
        </div >
      )
    default: return;
  }
}

export default ConfigurationLine;
